import { Activity, ArrowLeft, LogOut } from 'lucide-react';
import { Link, Outlet, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { languageRootPath } from '@app/i18n/languages';
import { LanguageSwitcher } from '@shared/components/LanguageSwitcher';
import { OfflineBanner } from '@shared/offline/OfflineBanner';
import { useAuth } from '@/hooks/useAuth';
import { useOnboarding } from '@features/billing/hooks/useOnboarding';

/**
 * Minimal chrome for the pre-payment funnel (add-station → subscribe). Unlike
 * AppLayout there is deliberately NO sidebar: a brand-new org that hasn't paid
 * shouldn't see the full product navigation. Just a thin header with the brand,
 * language switch and a sign-out escape hatch, plus the legal footer.
 */
export const OnboardingLayout = () => {
  const { t } = useTranslation();
  const { lng = 'es' } = useParams();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { data: onboarding } = useOnboarding();
  // Only an already-onboarded org (here to manage its subscription) gets a way
  // back to the app — a brand-new org would just be bounced back by the gate.
  const showBackToApp = onboarding?.next_step === 'completed';

  const handleLogout = async () => {
    // Navigate to the language root FIRST, then sign out — leaving the protected
    // subtree before the session clears stops ProtectedRoute from bouncing us to
    // /login the instant `user` becomes null (the landing is public).
    void navigate(languageRootPath(lng), { replace: true });
    await logout();
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 text-ink">
      <OfflineBanner />
      <header className="sticky top-0 z-10 border-b border-line bg-white/95 backdrop-blur">
        <div className="flex h-16 items-center justify-between gap-3 px-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <Activity className="h-5 w-5 text-brand-600" aria-hidden="true" />
            <span className="text-sm font-semibold">{t('app.workspace')}</span>
          </div>
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            {showBackToApp ? (
              <Link
                to={`/${lng}/dashboard`}
                className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                <span className="hidden sm:inline">{t('nav.backToApp')}</span>
              </Link>
            ) : null}
            {user ? (
              <span className="hidden text-sm text-muted md:inline">{user.email}</span>
            ) : null}
            <LanguageSwitcher />
            <button
              className="toolbar-button"
              type="button"
              aria-label={t('auth.logout')}
              title={t('auth.logout')}
              onClick={() => void handleLogout()}
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 py-10 sm:px-6 lg:px-8">
        <Outlet />
      </main>

      <footer className="border-t border-line px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center justify-between gap-2 text-xs text-muted sm:flex-row">
          <span>© {new Date().getFullYear()} LogBooks</span>
          <nav className="flex items-center gap-3">
            <Link
              to={`/${lng}/legal/privacy`}
              className="underline underline-offset-2 hover:text-ink"
            >
              {t('legal.privacyTitle')}
            </Link>
            <Link
              to={`/${lng}/legal/terms`}
              className="underline underline-offset-2 hover:text-ink"
            >
              {t('legal.termsTitle')}
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default OnboardingLayout;
